import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { message } from 'antd'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import CertificateOfCompletion from './Certificate'
import { formatDateOnly } from 'src/Common/FormatDateTime'
import 'src/Pages/LibraryPages/librarypages.scss'

const ACCENTS = ['a-blue', 'a-cyan', 'a-teal', 'a-amber', 'a-emerald', 'a-sky', 'a-navy']

const AwardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="8" r="6" />
    <path d="M8.21 13.89 7 22l5-3 5 3-1.21-8.12" />
  </svg>
)

const SealIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
)

const DownloadIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
)

const TrainingCertificates = () => {
  const [certificates, setCertificates] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [currentCertificate, setCurrentCertificate] = useState(null)

  useEffect(() => {
    const fetchCertificates = async () => {
      const token = localStorage.getItem('token')
      const role = localStorage.getItem('role')

      let url = ''

      if (role === 'Mentor') {
        url = `${process.env.REACT_APP_BASE_URL_API}/InternshipCertificate/Mentor/List/lookup`
      } else if (role === 'Learner') {
        url = `${process.env.REACT_APP_BASE_URL_API}/InternshipCertificate/Learner/List/lookup`
      } else if (role === 'Admin') {
        url = `${process.env.REACT_APP_BASE_URL_API}/InternshipCertificate/Admin/List/lookup`
      } else if (role === 'Counselor') {
        url = `${process.env.REACT_APP_BASE_URL_API}/InternshipCertificate/Counselor/List/lookup`
      } else {
        console.warn('Role not recognized:', role)
        setLoading(false)
        return
      }

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            accept: '*/*',
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        // api-dev2: paginated ({ items, ... }); tolerate a plain array too.
        setCertificates(Array.isArray(data) ? data : data?.items ?? [])
      } catch (err) {
        console.error('Error', err)
      } finally {
        setLoading(false)
      }
    }

    fetchCertificates()
  }, [])

  const handleDownload = (cert) => {
    setCurrentCertificate(cert)
    setDownloading(true)
  }

  const certificateRef = useRef(null)

  useLayoutEffect(() => {
    const generatePDF = async () => {
      if (!currentCertificate || !certificateRef.current) return

      try {
        // Add a slight delay to ensure rendering is complete
        await new Promise((resolve) => setTimeout(resolve, 100)) // 100ms delay

        const canvas = await html2canvas(certificateRef.current, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        })

        const imgData = canvas.toDataURL('image/png')
        const pdf = new jsPDF('landscape', 'mm', 'a4')
        const imgProps = pdf.getImageProperties(imgData)
        const pdfWidth = pdf.internal.pageSize.getWidth()
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)
        pdf.save(
          `${currentCertificate.firstName}_${
            currentCertificate.lastName
          }_${currentCertificate.projectTitle.replace(/\s+/g, '_')}.pdf`,
        )
      } catch (error) {
        console.error('Error generating PDF:', error)
        message.error('Failed to generate certificate. Please try again.')
      } finally {
        setDownloading(false)
        setCurrentCertificate(null)
      }
    }

    if (currentCertificate) {
      generatePDF()
    }
  }, [currentCertificate])

  return (
    <>
      {currentCertificate && (
        <div style={{ position: 'absolute', left: '-9999px' }}>
          <div ref={certificateRef}>
            <CertificateOfCompletion certificate={currentCertificate} />
          </div>
        </div>
      )}

      <div className="lpg">
        <div className="lpg__head">
          <div className="lpg__head-main">
            <span className="lpg__head-ic">
              <AwardIcon />
            </span>
            <div>
              <h1 className="lpg__title">Project Certificate</h1>
              <p className="lpg__sub">Project completion certificate.</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="lpg__state">
            <div className="lpg__spin" />
            Loading...
          </div>
        ) : certificates.length === 0 ? (
          <div className="lpg__state">No project certificates available yet.</div>
        ) : (
          <div className="lpg__grid">
            {certificates.map((cert, i) => {
              const isDownloading = downloading && currentCertificate?.id === cert.id
              return (
                <div key={cert.id} className={`lpg__card ${ACCENTS[i % ACCENTS.length]}`}>
                  <span className="lpg__ic">
                    <SealIcon />
                  </span>
                  <span className="lpg__ct">{cert.projectTitle}</span>
                  <div className="lpg__cd">
                    <div>
                      <strong>Recipient:</strong> {cert.firstName} {cert.lastName}
                    </div>
                    <div>
                      <strong>Completed:</strong> {formatDateOnly(cert.createdOn)}
                    </div>
                    <div>Certificate ID: {cert.id}</div>
                  </div>
                  <button
                    type="button"
                    className="lpg__btn-primary"
                    style={{ marginTop: 'auto' }}
                    onClick={() => handleDownload(cert)}
                    disabled={isDownloading}
                  >
                    <DownloadIcon />
                    {isDownloading ? 'Downloading...' : 'Download Certificate'}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}

export default TrainingCertificates
